<template>
  <VcPopover
    v-model:open="isShown"
    class="vc-tooltip"
    :class="`vc-tooltip--${dynamicPlacement}`"
    :placement="dynamicPlacement"
    :strategy="strategy"
    :hover="false"
    trigger="hover"
    :offset-options="offsetOptions"
    :flip-options="flipOptions"
    :shift-options="shiftOptions"
    :width="width"
  >
    <template #trigger>
      <div ref="triggerElement" @click="toggleTooltip">
        <slot name="trigger" />
      </div>
    </template>

    <template #content>
      <div ref="tooltipContent" class="vc-tooltip__content">
        <div>
          <div v-if="$slots.title" class="vc-tooltip__title">
            <slot name="title" />
          </div>
          <div class="vc-tooltip__description">
            <slot name="content" />
          </div>
        </div>
      </div>
    </template>
  </VcPopover>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";

export interface IEmits {
  (event: "shown", isShown: boolean): void;
}

export interface IProps {
  placement?: VcTooltipPlacementType;
  strategy?: VcTooltipStrategyType;
  flipOptions?: VcTooltipFlipOptionsType;
  offsetOptions?: VcTooltipOffsetOptionsType;
  shiftOptions?: VcTooltipShiftOptionsType;
  hover?: boolean;
  width?: string;
}

const emit = defineEmits<
  IEmits & {
    (event: "update:open", value: boolean): void;
  }
>();

const props = withDefaults(defineProps<IProps>(), {
  placement: "right",
  offsetOptions: 6,
  hover: false, // force hover to false
});

const isShown = ref(false);
const dynamicPlacement = ref(props.placement);
const tooltipContent = ref<HTMLElement | null>(null);
const triggerElement = ref<HTMLElement | null>(null);
function toggleTooltip() {
  emit("update:open", !isShown.value);
}

function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (
    tooltipContent.value &&
    triggerElement.value &&
    !tooltipContent.value.contains(target) &&
    !triggerElement.value.contains(target)
  ) {
    isShown.value = false;
  }
}

function updateDynamicPlacement() {
  if (props.placement === "top") {
    dynamicPlacement.value = "top";
  } else {
    dynamicPlacement.value = window.innerWidth < 768 ? "top" : props.placement || "right";
  }
}

onMounted(() => {
  updateDynamicPlacement();
  window.addEventListener("resize", updateDynamicPlacement);
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  window.removeEventListener("resize", updateDynamicPlacement);
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style lang="scss">
.vc-tooltip {
  @apply relative leading-none text-[--color-neutral-a1];
  .vc-popover__content {
    @apply w-[224px] #{!important};
  }
  &__title {
    @apply text-xs leading-5 font-bold py-1.5 px-3 bg-[--color-neutral-a6] rounded-t-md text-[--color-neutral-a1];
  }

  &__description {
    @apply text-xs leading-5 pt-1.5 pb-2.5 px-3 font-medium text-[--color-neutral-a1];
  }
  &__content {
    @apply max-w-full rounded-sm bg-[--color-white] text-xs text-gray-800 relative rounded-md border border-[--color-neutral-a4];
  }

  &--top {
    &:has(.vc-popover__content.position-bottom) {
      .vc-tooltip__content {
        @apply mt-1;
        &::before,
        &::after {
          @apply bottom-full left-1/2 border-solid h-0 w-0 absolute pointer-events-none;
          content: "";
        }
        &::after {
          @apply border-transparent border-b-[--color-white] border-[7px] ml-[-7px];
        }
        &::before {
          @apply border-transparent border-b-[--color-neutral-a06] border-8 ml-[-8px];
        }
      }
    }

    &:has(.vc-popover__content.position-top) {
      .vc-tooltip__content {
        @apply mb-1;
        &::before,
        &::after {
          @apply top-full left-1/2 border-solid h-0 w-0 absolute pointer-events-none;
          content: "";
        }

        &::after {
          @apply border-transparent border-t-[--color-white] border-[7px] ml-[-7px];
        }

        &::before {
          @apply border-transparent border-t-[--color-neutral-a06] border-8 ml-[-8px];
        }
      }
    }
  }

  &--bottom {
    .vc-tooltip__content {
      @apply mt-1;
      &::before,
      &::after {
        @apply bottom-full left-1/2 border-solid h-0 w-0 absolute pointer-events-none;
        content: "";
      }
      &::after {
        @apply border-transparent border-b-[--color-white] border-[7px] ml-[-7px];
      }
      &::before {
        @apply border-transparent border-b-[--color-neutral-a06] border-8 ml-[-8px];
      }
    }
  }

  &--left {
    .vc-tooltip__content {
      @apply mr-1;
      &::before,
      &::after {
        @apply left-full top-1/2 border-solid h-0 w-0 absolute pointer-events-none;
        content: "";
      }
      &::after {
        @apply border-transparent border-l-[--color-white] border-[7px] mt-[-7px];
      }
      &::before {
        @apply border-transparent border-l-[--color-neutral-a06] border-8 mt-[-8px];
      }
    }
  }

  &--right {
    .vc-tooltip__content {
      @apply ml-1;
      &::before,
      &::after {
        @apply right-full top-1/2 border-transparent border-solid h-0 w-0 absolute pointer-events-none;
        content: "";
      }
      &::after {
        @apply border-transparent border-r-[--color-white] border-[7px] mt-[-7px];
      }
      &::before {
        @apply border-transparent border-r-[--color-neutral-a06] border-8 mt-[-8px];
      }
    }
  }
}
</style>
